// Story 056 — main-thread client for the histogram Web Worker.
//
// This module is the WORKER SEAM: it is the only place that spawns the
// worker (`new Worker(new URL(...), { type: "module" })` — Vite's native
// module-worker pattern), so jsdom component tests mock this module (or
// the `useImageHistogram` hook above it) instead of needing a real
// worker/canvas.
//
// Staleness contract: `compute` is last-write-wins. Posting a new request
// SUPERSEDES any in-flight one — the older promise rejects with
// `StaleComputeError` and the worker's eventual answer for it is
// discarded by requestId. This is what lets the widget switch images
// mid-compute without ever painting a stale histogram.
import type {
  HistogramWorkerRequest,
  HistogramWorkerResponse,
} from "../../../workers/histogram.worker.js";
import type { HistogramResult } from "./computeHistogram.js";

/** Rejection sentinel for a compute superseded by a newer request. */
export class StaleComputeError extends Error {
  constructor() {
    super("histogram compute superseded by a newer request");
    this.name = "StaleComputeError";
    Object.setPrototypeOf(this, StaleComputeError.prototype);
  }
}

export interface HistogramClient {
  /**
   * Bucket the given downsampled pixels off-thread. The `ImageData`'s
   * underlying buffer is TRANSFERRED to the worker (zero-copy) — the
   * caller must not reuse it afterwards.
   */
  compute: (image: ImageData) => Promise<HistogramResult>;
  /** Terminate the worker and reject any in-flight compute. */
  dispose: () => void;
}

interface Pending {
  requestId: number;
  resolve: (result: HistogramResult) => void;
  reject: (error: Error) => void;
}

/** One worker per client; the widget owns exactly one client per mount. */
export function createHistogramClient(): HistogramClient {
  let worker: Worker | null = null;
  let nextRequestId = 1;
  let pending: Pending | null = null;

  function ensureWorker(): Worker {
    if (worker) return worker;
    worker = new Worker(
      new URL("../../../workers/histogram.worker.ts", import.meta.url),
      { type: "module" },
    );
    worker.onmessage = (event: MessageEvent<HistogramWorkerResponse>) => {
      const { requestId, l, r, g, b } = event.data;
      // Stale answer (the analyst already moved on) — drop it.
      if (pending?.requestId !== requestId) return;
      const { resolve } = pending;
      pending = null;
      resolve({ l, r, g, b });
    };
    worker.onerror = () => {
      // A worker-level error (e.g. the script failed to load) leaves the
      // Worker instance dead — reject the in-flight compute AND tear the
      // worker down so the next compute re-spawns a fresh one instead of
      // posting into a corpse that never answers.
      if (pending) {
        const { reject } = pending;
        pending = null;
        reject(new Error("histogram worker failed"));
      }
      worker?.terminate();
      worker = null;
    };
    return worker;
  }

  return {
    compute: (image: ImageData) => {
      const requestId = nextRequestId;
      nextRequestId += 1;
      // Supersede any in-flight request — last-write-wins.
      if (pending) {
        pending.reject(new StaleComputeError());
        pending = null;
      }
      return new Promise<HistogramResult>((resolve, reject) => {
        pending = { requestId, resolve, reject };
        const request: HistogramWorkerRequest = {
          requestId,
          // This one line is typed DIFFERENTLY by the two packages that
          // compile this file, so it needs both the assertion and the
          // suppression:
          //
          //   apps/web  — `react-app.json` sets `types: ["vite/client"]`, which
          //               excludes @types/node. TypeScript 5.7 made TypedArrays
          //               generic over their buffer, so lib.dom gives
          //               `Uint8ClampedArray<ArrayBufferLike>` and `.buffer` is
          //               `ArrayBufferLike` → the assertion is REQUIRED.
          //   apps/lyse — `react.json` sets no `types`, so @types/node is auto
          //               -included and `.buffer` narrows to `ArrayBuffer`
          //               → the same assertion is "unnecessary" and eslint
          //               errors on it.
          //
          // Asserting is safe: a canvas-produced ImageData is always backed by
          // a plain ArrayBuffer, and only ArrayBuffer is `Transferable`, which
          // the transfer list below requires — widening the interface to
          // ArrayBufferLike would break that transfer instead.
          //
          // The real defect is that the two tsconfigs resolve different lib
          // types for shared lyse sources; that is worth fixing centrally, but
          // not from a hotfix that exists to get `main` green again.
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion -- necessary under apps/web's tsconfig; see above
          buffer: image.data.buffer as ArrayBuffer,
        };
        ensureWorker().postMessage(request, [request.buffer]);
      });
    },
    dispose: () => {
      if (pending) {
        pending.reject(new StaleComputeError());
        pending = null;
      }
      worker?.terminate();
      worker = null;
    },
  };
}
